import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { DEFAULT_PAGE_LIMIT } from "@/shared/ui/OffsetPaginationBar";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, SendIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  useEligibleNotificationsQuery,
  useEnqueueNotifications,
  useProcessNotificationQueue,
  useWhatsAppWorkerStatusQuery,
} from "../api/queries";
import { DEFAULT_ELIGIBLE_NOTIFICATIONS_FILTERS } from "../lib/eligible-notifications-filters";
import { EligibleNotificationsFilterBar } from "../ui/EligibleNotificationsFilterBar";
import {
  EligibleNotificationsTable,
  eligibleNotificationRowKey,
} from "../ui/EligibleNotificationsTable";
import { NotificationPreviewModal } from "../ui/NotificationPreviewModal";
import { SendConfirmDialog } from "../ui/SendConfirmDialog";
import { WorkerStatusBanner } from "../ui/WorkerStatusBanner";

export function PackageNotificationsSendPage() {
  const nav = useNavigate();
  const workerQuery = useWhatsAppWorkerStatusQuery();
  const enqueue = useEnqueueNotifications();
  const processQueue = useProcessNotificationQueue();

  const [filters, setFilters] = useState(DEFAULT_ELIGIBLE_NOTIFICATIONS_FILTERS);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = DEFAULT_PAGE_LIMIT;
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItems, setPreviewItems] = useState<
    NonNullable<ReturnType<typeof useEligibleNotificationsQuery>["data"]>["items"]
  >([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), 300);
    return () => clearTimeout(timer);
  }, [filters.search]);

  useEffect(() => {
    setOffset(0);
  }, [filters.packageType, filters.triggerType, debouncedSearch]);

  const eligibleQuery = useEligibleNotificationsQuery({
    packageType:
      filters.packageType === "all" ? undefined : filters.packageType,
    triggerType:
      filters.triggerType === "all" ? undefined : filters.triggerType,
    search: debouncedSearch || undefined,
    limit,
    offset,
  });

  const items = eligibleQuery.data?.items ?? [];
  const meta = eligibleQuery.data?.meta;
  const stats = eligibleQuery.data?.stats;
  const totalAll =
    (stats?.byPackageType.package ?? 0) +
    (stats?.byPackageType.private_lesson ?? 0);

  const selectedItems = useMemo(
    () => items.filter((item) => selected.has(eligibleNotificationRowKey(item))),
    [items, selected],
  );

  const handleFiltersChange = (
    next: typeof DEFAULT_ELIGIBLE_NOTIFICATIONS_FILTERS,
  ) => {
    setFilters(next);
    if (
      next.packageType !== filters.packageType ||
      next.triggerType !== filters.triggerType
    ) {
      setOffset(0);
    }
  };

  const handleSend = async () => {
    if (selectedItems.length === 0) return;
    try {
      const enqueueResult = await enqueue.mutateAsync({
        items: selectedItems.map((item) => ({
          studentId: item.studentId,
          packageType: item.packageType,
          packageId: item.packageId,
          triggerType: item.triggerType,
        })),
      });

      let sent = 0;
      let failed = 0;
      let paused = false;
      let pauseReason: string | undefined;
      const idsToProcess = enqueueResult.queueIds ?? [];
      for (let i = 0; i < idsToProcess.length; i += 1) {
        const result = await processQueue.mutateAsync({
          batchSize: 1,
          queueIds: [idsToProcess[i]!],
        });
        sent += result.sent;
        failed += result.failed;
        if (result.paused) {
          paused = true;
          pauseReason = result.pauseReason;
          break;
        }
      }

      if (paused) {
        toast.warning(
          `enqueue ${enqueueResult.enqueued}, sent ${sent} — Worker ບໍ່ພ້ອມ (${pauseReason ?? "paused"})`,
        );
      } else {
        toast.success(
          `enqueue ${enqueueResult.enqueued}, sent ${sent}, skipped ${enqueueResult.skipped.length}, failed ${failed}`,
        );
      }
      setConfirmOpen(false);
      setSelected(new Set());
    } catch {
      toast.error("ສົ່ງບໍ່ສຳເລັດ");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            {
              label: "ແຈ້ງເຕືອນແພັກ",
              to: "/app/package-notifications",
            },
            { label: "ສົ່ງ Manual" },
          ]}
        />
      </Header>
      <Main>
        <div className="mx-auto w-full max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="font-bold text-2xl tracking-tight">ສົ່ງ Manual</h2>
                <WorkerStatusBanner health={workerQuery.data} />
              </div>
              <p className="text-muted-foreground">
                ເລືອກລາຍການແລ້ວສົ່ງຜ່ານ WhatsApp Worker
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/package-notifications" })}
            >
              <ArrowLeftIcon className="mr-1 size-4" />
              ກັບຄືນ
            </Button>
          </div>

          <QueryState
            result={eligibleQuery}
            title="ກຳລັງໂຫຼດ"
            description="ດຶງ eligible list"
          >
            <Card className="gap-0">
              <CardHeader className="space-y-1 pb-3">
                <CardTitle className="text-base">ເລືອກລາຍການທີ່ຈະສົ່ງ</CardTitle>
                <CardDescription>
                  ກັ່ນຕົວແລ້ວເລືອກລາຍການ — ກວດ preview ກ່ອນສົ່ງ
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                <EligibleNotificationsFilterBar
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  stats={stats}
                  filteredTotal={meta?.total ?? 0}
                  totalAll={totalAll}
                />
                <EligibleNotificationsTable
                  items={items}
                  selected={selected}
                  onSelectedChange={setSelected}
                  onPreview={(rows) => {
                    setPreviewItems(rows);
                    setPreviewOpen(true);
                  }}
                  canSend
                  totalCount={totalAll}
                  offset={offset}
                  limit={limit}
                  filteredTotal={meta?.total ?? 0}
                  onPaginationChange={setOffset}
                />

                <div className="flex justify-end border-t pt-4">
                  <Button
                    disabled={selectedItems.length === 0}
                    onClick={() => setConfirmOpen(true)}
                  >
                    <SendIcon className="mr-1 size-4" />
                    ສົ່ງ WhatsApp ({selectedItems.length})
                  </Button>
                </div>
              </CardContent>
            </Card>
          </QueryState>
        </div>

        <NotificationPreviewModal
          open={previewOpen}
          onOpenChange={setPreviewOpen}
          items={previewItems}
        />
        <SendConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          count={selectedItems.length}
          submitting={enqueue.isPending || processQueue.isPending}
          onConfirm={() => void handleSend()}
        />
      </Main>
    </>
  );
}

export default PackageNotificationsSendPage;
